import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { mcpCall } from '../lib/mcp';
import './Dashboard.css';

/**
 * Operations overview. Every figure here is pulled LIVE from the MCP read tools —
 * there are no mock/placeholder metrics. Counts use a high page limit so the full
 * catalog is reflected; a zero (e.g. inventory) is a true empty with a clear hint,
 * not a stub. Each card links to the section that owns the detail.
 */

// The MCP read tools cap `limit` at 500 (pagination schema). The catalog is well
// under that, so a page count is the true total; if one ever hits the cap we show "500+".
const PAGE_LIMIT = 500;

interface Stat {
  key: string;
  label: string;
  tool: string;
  select: (r: unknown) => unknown[];
  hint: string;
  to: string;
  warnIfPositive?: boolean;
}

const STATS: Stat[] = [
  {
    key: 'skus',
    label: 'SKUs in Catalog',
    tool: 'listSkus',
    select: (r) => (r as { skus?: unknown[] }).skus ?? [],
    hint: 'Sellable SKUs with UPC and Amazon ASIN.',
    to: '/products',
  },
  {
    key: 'gaps',
    label: 'Listing Gaps',
    tool: 'listSkuListingGaps',
    select: (r) => (r as { gaps?: unknown[] }).gaps ?? [],
    hint: 'Active SKUs missing an Amazon ASIN.',
    to: '/listing-gaps',
    warnIfPositive: true,
  },
  {
    key: 'inventory',
    label: 'Inventory Records',
    tool: 'listCurrentInventory',
    select: (r) => (r as { inventory?: unknown[] }).inventory ?? [],
    hint: 'Per-warehouse on-hand rows. Empty until baselines are loaded.',
    to: '/inventory',
  },
];

function StatCard({ stat }: { stat: Stat }) {
  const query = useQuery({
    queryKey: ['mcp', stat.tool, 'dashboard'],
    queryFn: async () => stat.select(await mcpCall(stat.tool, { limit: PAGE_LIMIT })).length,
  });

  const warn = stat.warnIfPositive && query.isSuccess && query.data > 0;
  const display = query.isSuccess ? `${query.data}${query.data >= PAGE_LIMIT ? '+' : ''}` : '';

  return (
    <Link to={stat.to} className="dash-stat">
      <span className="dash-stat__label">{stat.label}</span>
      <span className="dash-stat__value" aria-live="polite">
        {query.isPending && <span className="dash-stat__spinner" aria-hidden="true" />}
        {query.isError && <span className="dash-stat__num dash-stat__num--muted">—</span>}
        {query.isSuccess && (
          <span className={`dash-stat__num${warn ? ' dash-stat__num--warn' : ''}`}>{display}</span>
        )}
      </span>
      <span className="dash-stat__hint">
        {query.isError ? `Couldn’t load — ${(query.error as Error).message}` : stat.hint}
      </span>
    </Link>
  );
}

const LINKS = [
  { to: '/products', label: 'Products', desc: 'Browse the SKU catalog and channel identifiers.' },
  { to: '/listing-gaps', label: 'Listing Gaps', desc: 'Find SKUs missing an Amazon ASIN.' },
  { to: '/inventory', label: 'Inventory', desc: 'On-hand by warehouse (once baselines are loaded).' },
];

export function Dashboard() {
  return (
    <div className="dashboard-page">
      <header className="dashboard-head">
        <h1 className="dashboard-title">Operations Overview</h1>
        <p className="dashboard-sub">
          A live, at-a-glance view of catalog health — SKU coverage, listing gaps, and on-hand
          inventory — pulled in real time from the MCP. Select any card to dig into that section.
        </p>
      </header>

      <div className="dash-stats">
        {STATS.map((s) => (
          <StatCard key={s.key} stat={s} />
        ))}
      </div>

      <section className="dash-links">
        <h2 className="dash-links__title">Where To Go</h2>
        <div className="dash-links__grid">
          {LINKS.map((l) => (
            <Link key={l.to} to={l.to} className="dash-link">
              <span className="dash-link__label">{l.label}</span>
              <span className="dash-link__desc">{l.desc}</span>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
